import { ColDef } from 'ag-grid-community'
import { styled } from '@mui/material'
import { colorPalette } from '../../../../style/colorPalette'
import { AggregateField } from '../../../../../domain/entity/WbsItemEntity'
import {
  AggregateTargetToggleGroup,
  AggregateFieldToggleGroup,
  WorkloadUnitToggleGroup,
} from '../../../../components/toggleGroups'
import FilterButton from '../../../../containers/BulkSheetView/components/header/FilterButton'
import SortButton from '../../../../containers/BulkSheetView/components/header/SortButton'
import ColumnSettingButton from '../../../../containers/BulkSheetView/components/header/ColumnSettingButton'
import DisplayFormatButton from '../../../../containers/BulkSheetView/components/header/DisplayFormatButton'
import ReloadButton from '../../../../containers/BulkSheetView/components/header/ReloadButton'
import { useSelector } from 'react-redux'
import { AllState } from '../../../../../store'
import { getFinancialStartMonth } from '../../../../../utils/date'
import { GanttChartParameter } from '../../../../containers/BulkSheetView/components/header/GanttChartParameter'
import { SortedColumnState } from '../../../../model/bulkSheetColumnSortState'
import { ProgramManagementPageStateApi } from '../../hooks/usePageState'
import { ProgramSelector } from './ProgramSelector'
import { Program } from '../../programManagement'

type HeaderProps = {
  programs: Program[]
  selectedProgramUuid: string | undefined
  onSelectProgram: (uuid: string | undefined) => void
  onReload: () => void
  pageStateApi: ProgramManagementPageStateApi
  isRowHeightFixed: boolean
  onSwitchingGanttChart: (value: boolean) => void
  filteredColumns: ColDef[]
  onDeleteFilteredColumn: (column: ColDef) => void
  resetFilters: () => void
  sortColumnsState: SortedColumnState[]
  onDeleteSortedColumn: (colId: string | ColDef<any>) => void
  onDeleteSortedAllColumns: () => void
  onChangeSortColumnState: (
    colId: string | ColDef<any>,
    sort: 'asc' | 'desc' | null
  ) => void
  onClickColumnSettingButton: (e: React.MouseEvent) => void
  columnSettingOpen: boolean
  isDependencyCriticalPathEnabled?: boolean
  onOpenCriticalPathDialog?: () => void
  onOptimizeGanttColumnWidth?: () => void
  rootScheduledStartDate?: string
  rootScheduledEndDate?: string
}

export const Header = ({
  programs,
  selectedProgramUuid,
  onSelectProgram,
  onReload,
  pageStateApi,
  isRowHeightFixed,
  onSwitchingGanttChart,
  filteredColumns,
  onDeleteFilteredColumn,
  resetFilters,
  sortColumnsState,
  onDeleteSortedColumn,
  onDeleteSortedAllColumns,
  onChangeSortColumnState,
  onClickColumnSettingButton,
  columnSettingOpen,
  isDependencyCriticalPathEnabled,
  onOpenCriticalPathDialog,
  onOptimizeGanttColumnWidth,
  rootScheduledStartDate,
  rootScheduledEndDate,
}: HeaderProps) => {
  const fiscalStartMonth = useSelector((state: AllState) =>
    getFinancialStartMonth(state.tenant.organization?.financialYearEndMonth)
  )
  return (
    <Root>
      <ConditionRow>
        <ProgramSelector
          programs={programs}
          selectedProgramUuid={selectedProgramUuid}
          onSelect={onSelectProgram}
        />
      </ConditionRow>
      <ToolbarRow>
        <ReloadButton onReload={onReload} />
        <AggregateTargetToggleGroup
          value={pageStateApi.aggregateTarget}
          onChange={pageStateApi.setAggregateTarget}
        />
        <AggregateFieldToggleGroup
          value={pageStateApi.aggregateField}
          onChange={pageStateApi.setAggregateField}
        />
        <WorkloadUnitToggleGroup
          value={pageStateApi.workloadUnit}
          refresh={pageStateApi.setWorkloadUnit}
          disabled={
            pageStateApi.aggregateField === AggregateField.WBS_ITEM_COUNT
          }
        />
        <FilterButton
          filteredColumns={filteredColumns}
          onDeleteFilteredColumn={onDeleteFilteredColumn}
          resetFilters={resetFilters}
        />
        <SortButton
          sortColumnsState={sortColumnsState}
          onDeleteSortedColumn={onDeleteSortedColumn}
          onDeleteSortedAllColumns={onDeleteSortedAllColumns}
          onChangeSortColumnState={onChangeSortColumnState}
        />
        <ColumnSettingButton
          onClick={onClickColumnSettingButton}
          open={columnSettingOpen}
        />
        <DisplayFormatButton
          height={pageStateApi.rowHeight}
          onChangeHeight={pageStateApi.setRowHeight}
          isRowHeightFixed={isRowHeightFixed}
          currentFormat={pageStateApi.dateFormat}
          onChangeDateFormat={pageStateApi.setDateFormat}
        />
        <GanttChartParameter
          onSwitchingGanttChart={onSwitchingGanttChart}
          value={pageStateApi.ganttVisible}
          parameter={pageStateApi.ganttParameter}
          onChangeParameter={pageStateApi.setGanttParameter}
          isDependencyCriticalPathEnabled={isDependencyCriticalPathEnabled}
          onOpenCriticalPathDialog={onOpenCriticalPathDialog}
          onOptimizeGanttColumnWidth={onOptimizeGanttColumnWidth}
          rootScheduledStartDate={rootScheduledStartDate}
          rootScheduledEndDate={rootScheduledEndDate}
          fiscalStartMonth={fiscalStartMonth}
        />
      </ToolbarRow>
    </Root>
  )
}

const rowBase = {
  display: 'flex',
  flexDirection: 'row' as const,
  alignItems: 'center',
  gap: '6px',
  flexWrap: 'nowrap' as const,
  overflowX: 'auto' as const,
  '& > *': {
    flexShrink: 0,
  },
}

const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  padding: '10px 0',
})

const ConditionRow = styled('div')({
  ...rowBase,
  padding: '10px',
  backgroundColor: `${colorPalette.monotone[2]}4D`,
})

const ToolbarRow = styled('div')({
  ...rowBase,
  padding: '0 10px',
})
